import React from 'react';
import Input from 'rsuite/esm/Input';
import Toggle from 'rsuite/esm/Toggle';
import { AcceptBlock, LangBlock, ShowNotify } from '../../global';
import { WebStorage } from '../../../storage';
import { AdminDataTable } from './admin.table';
import { AdminButton, AdminFormField, AdminPageWrap, AdminSection } from './admin.ui';
import { AdminAvatar } from './admin.user-actions';

type Props = {
    extensions: any[];
    loadExtensions: () => void;
};

type AddExtensionsResponse = {
    status: boolean;
    error_message?: string;
    added?: string[];
    failed?: { input: string; error: string }[];
};

export const AdminExtensions = ({ extensions, loadExtensions }: Props) => {
    const [, , LangString] = LangBlock();
    const [extensionAdd, setExtensionAdd] = React.useState('');
    const [adding, setAdding] = React.useState(false);
    const [exportOpen, setExportOpen] = React.useState(false);
    const [exportText, setExportText] = React.useState('');

    const formatExtensionForExport = (row: { repo: string; example?: boolean }) =>
        row.example ? `${row.repo}|example` : row.repo;

    const handleExportExtensions = () => {
        setExportText(extensions.map(formatExtensionForExport).join('\n'));
        setExportOpen(true);
    };

    const handleAddExtensions = () => {
        const text = extensionAdd.trim();

        if (!text || adding) {
            return;
        }

        setAdding(true);

        WebStorage.ApiRequestPost('/api/admin/extensions/add', {
            repos: extensionAdd,
        }).then((res: AddExtensionsResponse) => {
            setAdding(false);

            if (!res.status) {
                ShowNotify(LangString('AdminAddExtNotify'), res.error_message || LangString('AdminAddExtFailed'), 'error');

                return;
            }

            const added = res.added || [];
            const failed = res.failed || [];

            if (added.length > 0) {
                loadExtensions();
            }

            if (failed.length > 0) {
                setExtensionAdd(failed.map((item) => item.input).join('\n'));
            } else {
                setExtensionAdd('');
            }

            if (added.length > 0 && failed.length === 0) {
                ShowNotify(
                    LangString('AdminAddExtNotify'),
                    LangString('AdminAddExtAdded', added.length, added.join(', ')),
                    'success',
                );
            } else if (added.length > 0 && failed.length > 0) {
                ShowNotify(
                    LangString('AdminAddExtNotify'),
                    LangString('AdminAddExtPartial', added.length, added.join(', '), failed.length),
                    'warning',
                );
            } else if (failed.length > 0) {
                ShowNotify(
                    LangString('AdminAddExtNotify'),
                    failed.map((item) => `${item.input}: ${item.error}`).join('\n'),
                    'error',
                );
            }
        });
    };

    return (
        <AdminPageWrap>
            {exportOpen ? (
                <div className="modalBG">
                    <div
                        className="modalBG2"
                        onClick={(e) => {
                            e.preventDefault();
                            setExportOpen(false);
                        }}
                    />
                    <div className="modalMain">
                        <div className="title">{LangString('AdminExportTitle')}</div>
                        <div className="content">
                            <Input
                                as="textarea"
                                rows={12}
                                value={exportText}
                                readOnly
                                style={{ width: 'min(90vw, 640px)' }}
                            />
                            <AdminButton
                                variant="ghost"
                                onClick={() => setExportOpen(false)}
                                style={{ marginTop: 8 }}
                            >
                                {LangString('AdminClose')}
                            </AdminButton>
                        </div>
                    </div>
                </div>
            ) : null}
            <AdminSection
                title={LangString('AdminExtTitle')}
                subtitle={LangString('AdminExtSubtitle')}
                actions={
                    <AdminButton
                        variant="ghost"
                        size="sm"
                        disabled={extensions.length === 0}
                        onClick={handleExportExtensions}
                    >
                        {LangString('AdminExportList')}
                    </AdminButton>
                }
            >
                <AdminDataTable
                    data={extensions}
                    rowKey={(row) => row.id}
                    emptyMessage={LangString('AdminNoExtensions')}
                    columns={[
                        {
                            key: 'icon',
                            label: LangString('AdminIcon'),
                            hideMobile: true,
                            render: (row) => (
                                <AdminAvatar src={`/api/extensions/icon/${row.repo}`} alt="" />
                            ),
                        },
                        {
                            key: 'name',
                            label: LangString('AdminName'),
                            primary: true,
                            render: (row) => (
                                <a
                                    className="admin-link"
                                    href={`https://github.com/${row.repo}`}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {typeof row.name === 'string' ? row.name : LangString(row.name)}
                                </a>
                            ),
                        },
                        {
                            key: 'type',
                            label: LangString('AdminType'),
                            render: (row) => row.type,
                        },
                        {
                            key: 'version',
                            label: LangString('AdminVersion'),
                            render: (row) => row.version,
                        },
                        {
                            key: 'example',
                            label: LangString('AdminExample'),
                            render: (row) => (
                                <Toggle
                                    checked={!!row.example}
                                    size="sm"
                                    onChange={async (checked) => {
                                        if (!(await AcceptBlock())) {
                                            return;
                                        }

                                        WebStorage.ApiRequest('/api/admin/extensions/setExample', {
                                            extension: row.id,
                                            example: checked ? '1' : '0',
                                        }).then((res) => {
                                            if (!res.status) {
                                                ShowNotify(
                                                    LangString('AdminExampleStatus'),
                                                    res.error_message || LangString('AdminUpdateFailed'),
                                                    'error',
                                                );

                                                return;
                                            }

                                            loadExtensions();
                                        });
                                    }}
                                />
                            ),
                        },
                        {
                            key: 'update',
                            label: LangString('AdminUpdate'),
                            render: (row) => (
                                <AdminButton
                                    variant="primary"
                                    size="sm"
                                    onClick={async (e) => {
                                        e.preventDefault();

                                        if (!(await AcceptBlock())) {
                                            return;
                                        }

                                        WebStorage.ApiRequest('/api/admin/extensions/update', {
                                            extension: row.id,
                                        }).then((res) => {
                                            if (!res.status) {
                                                ShowNotify(
                                                    LangString('AdminSyncRepo'),
                                                    res.error_message || LangString('AdminSyncFailed'),
                                                    'error',
                                                );

                                                return;
                                            }

                                            loadExtensions();

                                            const name =
                                                typeof row.name === 'string'
                                                    ? row.name
                                                    : LangString(row.name);

                                            if (res.updated) {
                                                ShowNotify(
                                                    LangString('AdminSyncRepo'),
                                                    LangString(
                                                        'AdminSyncUpdated',
                                                        name,
                                                        res.data?.version ?? LangString('AdminLatest'),
                                                    ),
                                                    'success',
                                                );
                                            } else {
                                                ShowNotify(
                                                    LangString('AdminSyncRepo'),
                                                    LangString(
                                                        'AdminSyncUnchanged',
                                                        name,
                                                        res.data?.version ?? row.version,
                                                    ),
                                                    'info',
                                                );
                                            }
                                        });
                                    }}
                                >
                                    {LangString('AdminSyncRepo')}
                                </AdminButton>
                            ),
                        },
                        {
                            key: 'remove',
                            label: LangString('AdminRemove'),
                            render: (row) => (
                                <AdminButton
                                    variant="danger"
                                    size="sm"
                                    onClick={async (e) => {
                                        e.preventDefault();

                                        if (!(await AcceptBlock())) {
                                            return;
                                        }

                                        WebStorage.ApiRequest('/api/admin/extensions/delete', {
                                            extension: row.id,
                                        }).then((res) => {
                                            if (res.status) {
                                                loadExtensions();
                                            }
                                        });
                                    }}
                                >
                                    {LangString('AdminRemove')}
                                </AdminButton>
                            ),
                        },
                    ]}
                />

                <AdminFormField
                    label={LangString('AdminAddExtLabel')}
                    wide
                >
                    <Input
                        as="textarea"
                        rows={6}
                        placeholder={
                            'RocketMan-StreamKit/example_widget\nhttps://github.com/RocketMan-StreamKit/widget_first_message|example'
                        }
                        value={extensionAdd}
                        onChange={setExtensionAdd}
                    />
                    <AdminButton
                        variant="primary"
                        disabled={adding || !extensionAdd.trim()}
                        onClick={handleAddExtensions}
                        style={{ marginTop: 8 }}
                    >
                        {adding ? LangString('AdminAdding') : LangString('AdminAdd')}
                    </AdminButton>
                </AdminFormField>
            </AdminSection>
        </AdminPageWrap>
    );
};
